import { Alert, Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  useWorkOrderPlanning,
  type PlannedHoursBaselineSource,
  type WorkOrderPlanningService,
} from '@/hooks/useWorkOrderPlanning';
import { useNotify } from '@/stores/notificationStore';
import { formatHoursOrDash } from '@/utils/actualTime';

interface PlannedBaselineCardProps {
  workOrderId: string;
  canMark: boolean;
}

function sourceKey(
  source: PlannedHoursBaselineSource,
): 'planning.sourceQuoteSent' | 'planning.sourceMarkedPlanned' {
  return source === 'quote_sent' ? 'planning.sourceQuoteSent' : 'planning.sourceMarkedPlanned';
}

function latestBaseline(services: readonly WorkOrderPlanningService[]): {
  at: string;
  source: PlannedHoursBaselineSource;
} | null {
  let latest: { at: string; source: PlannedHoursBaselineSource } | null = null;
  for (const service of services) {
    if (!service.baselineAt || !service.baselineSource) continue;
    if (!latest || service.baselineAt > latest.at) {
      latest = { at: service.baselineAt, source: service.baselineSource };
    }
  }
  return latest;
}

function serviceOverruns(service: WorkOrderPlanningService): boolean {
  return service.baselineHours != null && service.actualHours > service.baselineHours;
}

export function PlannedBaselineCard({ workOrderId, canMark }: PlannedBaselineCardProps) {
  const { t } = useTranslation('workorders');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const { query, markPlanned } = useWorkOrderPlanning(workOrderId);
  const services = query.data?.services ?? [];
  const totals = query.data?.totals;
  const latest = latestBaseline(services);
  const hasBaseline = services.some((service) => service.baselineAt != null || service.baselineSource != null);
  const overrun = services.some(serviceOverruns);

  const onMark = () => {
    void markPlanned.mutateAsync().then(
      () => {
        notify.success(t('planning.markedTitle'), t('planning.markedDesc'));
      },
      (error: unknown) => {
        notify.error(tc('error'), describeApiError(error, tc, { fallback: t('planning.markFailed') }));
      },
    );
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="planned-baseline-card">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            {t('planning.title')}
          </Typography>
          {latest ? (
            <Typography variant="body2" color="text.secondary" data-testid="planned-baseline-frozen">
              {t('planning.frozenAt', {
                at: new Date(latest.at).toLocaleString(),
                source: t(sourceKey(latest.source)),
              })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" data-testid="planned-baseline-unfrozen">
              {t('planning.notFrozen')}
            </Typography>
          )}
        </Box>
        {canMark ? (
          <Button
            variant="outlined"
            size="small"
            onClick={onMark}
            disabled={markPlanned.isPending || query.isLoading}
            data-testid="planned-baseline-mark"
          >
            {hasBaseline ? t('planning.updatePlan') : t('planning.markPlanned')}
          </Button>
        ) : null}
      </Box>

      {query.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }} role="status" aria-label={tc('loading')}>
          <CircularProgress size={24} />
        </Box>
      ) : null}

      {query.isError ? (
        <Alert severity="error" sx={{ mt: 1 }} data-testid="planned-baseline-load-error">
          {describeApiError(query.error, tc, { fallback: t('planning.loadFailed') })}
        </Alert>
      ) : null}

      {overrun ? (
        <Alert severity="warning" sx={{ mt: 1 }} data-testid="planned-baseline-overrun">
          {t('planning.overrun')}
        </Alert>
      ) : null}

      {totals ? (
        <Box sx={{ mt: 1 }} data-testid="planned-baseline-totals">
          <Typography variant="body2">
            {t('planning.currentHours')}: {formatHoursOrDash(totals.plannedHours)}
            {' · '}
            {t('planning.baselineHours')}: {formatHoursOrDash(totals.baselineHours)}
            {' · '}
            {t('planning.actualHours')}: {formatHoursOrDash(totals.actualHours)}
          </Typography>
          {services.map((service) => (
            <Typography
              key={service.id}
              variant="body2"
              color="text.secondary"
              data-testid={`planned-baseline-service-${service.id}`}
              data-overrun={serviceOverruns(service) ? 'true' : 'false'}
            >
              {service.title}
              {': '}
              {t('planning.currentHours')} {formatHoursOrDash(service.plannedHours)}
              {', '}
              {t('planning.baselineHours')} {formatHoursOrDash(service.baselineHours)}
              {', '}
              {t('planning.actualHours')} {formatHoursOrDash(service.actualHours)}
            </Typography>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}
